import React, { useState, useEffect } from "react";
import { Layout } from "antd";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { updateTutorialStatusAction } from "@redux/actions/adminActions";
import { redirectToDashboard } from "@utils/roleBasedRedirect";
import logoSmall from "@assets/images/logo/logo.png";

import MenuLogo from "./logo";
import MenuFooter from "./footer";
import MenuItem from "./item";
import MenuMobile from "./mobile";
// Guide Tour
import { Tour } from "@components";
import {
  adminSidebarSteps,
  memberSidebarSteps,
} from "@helpers/shepherd/sidebarSteps";
import { fireConfetti } from "@utils/confetti";

const { Sider } = Layout;

const Sidebar = (props) => {
  const dispatch = useDispatch();

  const { visible, setVisible } = props;

  // Redux
  const customize = useSelector((state) => state.customize);
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [sidebarTour, setSidebarTour] = useState(() => {
    // Check localStorage first
    const shepherdData = localStorage.getItem("shepherd");
    if (shepherdData) {
      try {
        const parsed = JSON.parse(shepherdData);
        if (parsed?.sideBar) return true;
      } catch (e) {
        // If parsing fails, clear invalid data
        localStorage.removeItem("shepherd");
      }
    }
    // Then check user data
    return user?.tutorial_complete?.sideBar || false;
  });

  // Collapsed and pinned state
  const [collapsed, setCollapsed] = useState(true);
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    if (user) {
      setLoading(false);
    }
  }, [user]);

  // Hover handlers for sidebar expand/collapse
  const handleMouseEnter = () => {
    if (!isPinned) {
      setCollapsed(false);
    }
  };

  const handleMouseLeave = () => {
    if (!isPinned) {
      setCollapsed(true);
    }
  };

  useEffect(() => {
    const shepherdData = JSON.parse(localStorage.getItem("shepherd"));
    if (shepherdData) {
      // Check each tutorial status and update the server if it's not already updated
      if (!user?.tutorial_complete?.sideBar && sidebarTour) {
        dispatch(updateTutorialStatusAction(shepherdData));
      }
    }
  }, []);

  useEffect(() => {
    if (customize.sidebarCollapsed) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [customize]);

  // Mobile Sidebar
  const onClose = () => {
    setVisible(false);
  };

  // Menu - toggle pin state
  const toggle = () => {
    setIsPinned(!isPinned);
    setCollapsed(isPinned);
  };

  // Handle Sidebar Guide Tour finish
  const handleSidebarTourComplete = (_, completionStatus) => {
    if (completionStatus) {
      // Set sidebarTour status to true
      setSidebarTour(true);

      // Update LocalStorage guideTour status
      const existingData = localStorage.getItem("shepherd");
      let parsedData = {};
      if (existingData) {
        try {
          parsedData = JSON.parse(existingData);
        } catch (e) {
          parsedData = {};
        }
      }
      const updatedData = { ...parsedData, sideBar: true };

      localStorage.setItem("shepherd", JSON.stringify(updatedData));

      // Update server with tutorial status
      dispatch(updateTutorialStatusAction(updatedData));

      fireConfetti();
    }
  };

  return (
    <>
      {/* Guide Tour - Only show after onboarding and subscription are complete */}
      {user?.role !== "superadmin" &&
        user?.role !== "marketing" &&
        !loading &&
        !sidebarTour &&
        (user?.onboarding_complete || localStorage.getItem("copilotly_onboarding_complete") === "true") &&
        (user?.subscribe_complete || user?.appSumoUser || localStorage.getItem("copilotly_subscription_complete") === "true") && (
          <Tour
            steps={
              user?.role === "admin" ? adminSidebarSteps : memberSidebarSteps
            }
            tutComplete={sidebarTour}
            tourId="sidebarTour"
            handleTourComplete={handleSidebarTourComplete}
          />
        )}

      {user && (
        <>
          <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={210}
            className="hp-sidebar"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              background: "#fff",
              boxShadow:
                "1px 0 6px rgba(0, 0, 0, 0.06), 2px 0 12px rgba(0, 0, 0, 0.03)",
              borderRight: "none",
              zIndex: 100,
              transition: "all 0.2s ease-in-out",
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ type: "spring", duration: 0.3, delay: 0.1 }}
              className="hp-d-flex hp-h-100"
              style={{
                flexDirection: "column",
              }}
            >
              {/* Header: Logo + Pin Icon */}
              <div
                style={{
                  minHeight: "45px",
                  position: "relative",
                  width: "100%",
                }}
              >
                {/* Expanded Logo - fixed position, no movement during collapse */}
                <div
                  style={{
                    opacity: collapsed ? 0 : 1,
                    visibility: collapsed ? "hidden" : "visible",
                    position: "absolute",
                    left: "20px",
                    top: "50%",
                    transform: "translateY(-50%) scale(1.1)",
                    transformOrigin: "left center",
                    whiteSpace: "nowrap",
                    transition: "opacity 0.15s, visibility 0.15s",
                  }}
                >
                  <MenuLogo onClose={onClose} />
                </div>

                {/* Collapsed Logo - centered in collapsed sidebar (40px = 80px width / 2) */}
                <div
                  style={{
                    opacity: collapsed ? 1 : 0,
                    visibility: collapsed ? "visible" : "hidden",
                    position: "absolute",
                    left: "40px",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    transition: "opacity 0.15s, visibility 0.15s",
                  }}
                >
                  <Link to={redirectToDashboard(user)} onClick={onClose}>
                    <img
                      className="hp-logo"
                      src={logoSmall}
                      alt="logo"
                      style={{
                        backgroundColor: "transparent",
                      }}
                    />
                  </Link>
                </div>

                {/* Pin/Unpin Icon - fixed position, no movement during collapse */}
                {customize.sidebarCollapseButton && (
                  <div
                    style={{
                      opacity: collapsed ? 0 : 1,
                      visibility: collapsed ? "hidden" : "visible",
                      position: "absolute",
                      right: "11px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      marginTop: "7px",
                      transition: "opacity 0.15s, visibility 0.15s",
                    }}
                    onClick={toggle}
                    title={isPinned ? "Unpin sidebar" : "Pin sidebar open"}
                  >
                    {isPinned ? (
                      // Pinned icon (pushpin filled)
                      <svg
                        width="1.3em"
                        height="1.3em"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill="#0010f7"
                          d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2z"
                        />
                      </svg>
                    ) : (
                      // Unpinned icon (pushpin outline)
                      <svg
                        width="1.3em"
                        height="1.3em"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill="#636E71"
                          d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2zm-1.5 2h-5l1-1V4h3v9z"
                        />
                      </svg>
                    )}
                  </div>
                )}
              </div>

              {/* Sidebar Items */}
              <MenuItem onClose={onClose} collapsed={collapsed} />

              {/* Footer */}
              <MenuFooter
                onClose={onClose}
                collapsed={collapsed}
                mobileMenu={false}
              />
            </motion.div>
          </Sider>
          <MenuMobile onClose={onClose} visible={visible} />
        </>
      )}

      <style>
        {`
          /* ============================================
             SIDEBAR ICON FIX - PREVENT JUMPING
             All values MUST be identical for both states
             Only the sidebar width should animate
             ============================================ */

          /* OVERRIDE: Force children padding to 0 for BOTH states */
          .hp-sidebar .ant-layout-sider-children {
            padding-left: 0 !important;
          }

          /* OVERRIDE: Force menu margin to 0 for BOTH states */
          .hp-sidebar .ant-menu-root {
            margin-left: 0 !important;
          }

          /* OVERRIDE: Force SAME padding (28px) for ALL menu items in BOTH states */
          .hp-sidebar .ant-menu-item,
          .hp-sidebar .ant-menu-inline-collapsed > .ant-menu-item,
          .hp-sidebar .ant-menu-inline > .ant-menu-item {
            padding-left: 28px !important;
            display: flex !important;
            align-items: center !important;
            height: 40px;
          }

          /* Icon sizing - consistent for both states */
          .hp-sidebar .ant-menu-item svg,
          .hp-sidebar .ant-menu-item .ant-menu-item-icon,
          .hp-sidebar .ant-menu-item .anticon {
            min-width: 24px !important;
            max-width: 24px !important;
            min-height: 24px !important;
            max-height: 24px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            flex-shrink: 0 !important;
            margin-right: 0 !important;
          }

          /* Hide text in collapsed state */
          .collapsed-menu .ant-menu-item .ant-menu-title-content {
            opacity: 0;
            visibility: hidden;
            width: 0;
            transition: opacity 0.2s ease-in-out;
          }

          /* Show text in expanded state */
          .expanded-menu .ant-menu-item .ant-menu-title-content {
            opacity: 1;
            visibility: visible;
            transition: opacity 0.2s ease-in-out;
          }

          .ant-layout-sider-children {
            overflow-x: hidden;
            width: 100%;
          }
          .ant-menu-inline {
            width: 100%;
            border-right: none !important;
          }

          /* Tooltip styling */
          .menu-tooltip {
            z-index: 1001 !important;
          }
          .ant-tooltip {
            z-index: 1001 !important;
          }

          /* Dropdown styling for profile menu */
          .ant-dropdown {
            z-index: 1050 !important;
          }
          .menu-tooltip .ant-tooltip-inner {
            min-height: 24px;
            padding: 4px 10px;
            color: #fff;
            text-shadow: none;
            background-color: rgba(0, 0, 0, 0.85);
            border-radius: 4px;
          }
          .menu-tooltip .ant-tooltip-arrow {
            display: block !important;
          }

          /* Collapsed menu item cursor */
          .collapsed-menu-item {
            cursor: pointer !important;
          }
        `}
      </style>
    </>
  );
};

export default Sidebar;
